import { IAddress } from './address';
import { ILocation } from './location';
import { IPhones } from './phones';

export interface IUserProfile {
  AcceptedPublicationDataOnWebsiteBy7n: boolean;
  AcceptedStoringAndSendingDataBy7n: boolean;
  AcceptedMy7nEventInvitation: boolean;
  AcceptedMy7nNewsletter: boolean;
  Address: IAddress;
  BusinessNumber: string;
  CellNumber: string;
  CompanyName: string;
  CompanyWebsite: string;
  Email: string;
  HomeNumber: string;
  Locations: ILocation[];
  SkypeId: string;
}

export interface IPersonalDetails {
  FirstName: string;
  LastName: string;
  Email: string;
  Phones: IPhones;
  Address: IAddress;
}

export interface IPersonalDetailsResponse {
  FirstName: string;
  LastName: string;
  Email: string;
  MobilePhone: string;
  BusinessPhone: string;
  Address: IAddress;
}

export interface ITermsAndConditions {
  AcceptStoringAndSendingDataBy7n: boolean;
  AcceptMy7nEventInvitation: boolean;
  AcceptMy7nNewsletter: boolean;
  My7nConsents: boolean;
  My7nNewsletterRevokedAt: string;
  My7nEventInvitationRevokedAt: string;
  PublicationDataOnWebsiteBy7nRevokedAt: string;
  StoringAndSendingDataBy7nRevokedAt: string;
}

export interface IBusinessDetails {
  CompanyName: string;
  CompanyWebsite: string;
}

export interface IUpdateAvatarResponse {
  PortraitUrl: string;
}

export const MAX_LENGTHS = {
  email: 100,
  phone: 20,
  skype: 100,
  company_name: 100,
  website: 100,
};

export const MIN_LENGTHS = {
  phone: 7,
};

export interface IConsultantsTenantDetails {
  IsActive: boolean;
  PrivateMail: string;
  ConsultantUPN: string;
}

export enum EmailUpdateDialogStates {
  UpdateEmail = 'UpdateEmail',
  VerifyCode = 'VerifyCode',
}

export interface IEmailUpdate {
  RequestId: string;
  CodeInfo: {
    CodeDate: string;
    CodeNumber: number;
    ExpiresInMinutes: number;
  };
  RequestUpdate: {
    Loading: boolean;
    Error: boolean;
    Status: RequestEmailUpdateStatus;
  };
  CodeVerification: {
    Loading: boolean;
    Error: boolean;
    Status: EmailUpdateCodeVerificationStatus;
  };
  GenerateCode: {
    Loading: boolean;
    Error: boolean;
    Status: EmailUpdateGenerateCodeStatus;
  };
}

export enum RequestEmailUpdateStatus {
  InvalidEmail = 'InvalidEmail',
  EmailUpdated = 'EmailUpdated',
  RequestFailed = 'RequestFailed',
  UpdateNotAllowed = 'UpdateNotAllowed',
  EmailCollision = 'EmailCollision',
  RestrictedDomain = 'RestrictedDomain',
  VerificationCodeSent = 'VerificationCodeSent',
}

export enum EmailUpdateCodeVerificationStatus {
  EmailUpdated = 'EmailUpdated',
  InvalidCode = 'InvalidCode',
  CodeExpired = 'CodeExpired',
  RequestFailed = 'RequestFailed',
}

export enum EmailUpdateGenerateCodeStatus {
  VerificationCodeSent = 'VerificationCodeSent',
  RequestFailed = 'RequestFailed',
}

export type RequestEmailUpdateResponse = IBasicEmailUpdateResponse<RequestEmailUpdateStatus>;
export type EmailUpdateCodeVerificationResponse = Pick<
  IBasicEmailUpdateResponse<EmailUpdateCodeVerificationStatus>,
  'RequestId' | 'Status'
> & {
  NewEmail: string;
};
export type EmailUpdateGenerateCodeResponse = IBasicEmailUpdateResponse<EmailUpdateGenerateCodeStatus>;

export interface IBasicEmailUpdateResponse<S> {
  RequestId: string;
  Status: S;
  CodeInfo: {
    CodeDate: string;
    CodeNumber: number;
    ExpiresInMinutes: number;
  };
}

export const RequestEmailUpdateErrors = [
  // Backend
  RequestEmailUpdateStatus.InvalidEmail,
  RequestEmailUpdateStatus.EmailCollision,
  RequestEmailUpdateStatus.RestrictedDomain,
  RequestEmailUpdateStatus.UpdateNotAllowed,
  RequestEmailUpdateStatus.RequestFailed,
  // Frontend
  'emailNotValid',
  'required',
  'maxlength'
]

export const CodeVerificationErrors = [
  // Backend
  EmailUpdateCodeVerificationStatus.InvalidCode,
  EmailUpdateCodeVerificationStatus.CodeExpired,
  EmailUpdateCodeVerificationStatus.RequestFailed,

  // Frontend
  'required',
  'pattern'
]

export const GenerateCodeErrors = [
  EmailUpdateGenerateCodeStatus.RequestFailed,
]

export const ErrorMessages = {
    requestEmailUpdate: {
      errors: RequestEmailUpdateErrors,
      messages: {
        // Backend
        [RequestEmailUpdateStatus.InvalidEmail]: 'The email address is not valid. Please enter a valid email address.',
        [RequestEmailUpdateStatus.RequestFailed]: 'Failed to update email. Please try again later.',
        [RequestEmailUpdateStatus.UpdateNotAllowed]:
          'Email update is not allowed for your account. Please contact support.',
        [RequestEmailUpdateStatus.EmailCollision]:
          'The email address is already in use. Please enter a different email address.',
        [RequestEmailUpdateStatus.RestrictedDomain]:
          'Email addresses from this domain are not allowed. Please enter a different email address.',

        // Frontend
        emailNotValid: 'Please enter a valid email address.',
        required: 'Email address is required.',
        maxlength: `Email cannot be longer than ${MAX_LENGTHS.email} characters.`,
      },
    },
    codeVerification: {
      errors: CodeVerificationErrors,
      messages: {
        // Backend
        [EmailUpdateCodeVerificationStatus.InvalidCode]:
          'The verification code is invalid. Please check the code and try again.',
        [EmailUpdateCodeVerificationStatus.CodeExpired]:
          'The verification code has expired. Please request a new code.',
        [EmailUpdateCodeVerificationStatus.RequestFailed]: 'Failed to verify the code. Please try again later.',

        // Frontend
        required: 'This field is required.',
        pattern: 'The code must be a 7-digit number.',
      },
    },
    generateCode: {
      errors: GenerateCodeErrors,
      messages: {
        [EmailUpdateGenerateCodeStatus.RequestFailed]: 'Failed to generate verification code. Please try again later.',
      },
    },
  };